import { Box, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { GroupedHeaderList } from '@/components/lists/GroupedHeaderList/GroupedHeaderList';
import { TableCell, TableCellContext } from '@/components/lists/TableCell';
import { EmptyListItem } from '@/components/typography/EmptyListItem';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment } from '@/modules/summaryPanels/HypotheticalTransfersSummaryPanel/graphql/HypotheticalTransfersSummary.generated';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import {
  GetHypotheticalTransfers_EstateWaterfallTransferFragment,
  GetHypotheticalTransfers_HypotheticalCltFragment,
  GetHypotheticalTransfers_HypotheticalCrtFragment,
  GetHypotheticalTransfers_HypotheticalGratFragment,
  GetHypotheticalTransfers_HypotheticalInsuranceFragment,
} from './graphql/GetHypotheticalTransfers.generated';
import { SaleLoanWithProjections } from './hooks/useGetHypotheticalTransfers';
import { useStrategyItems } from './hooks/useStrategyItems';
import { OnEditStrategyTransferInput } from './HypotheticalTransfersCard';
import { TransferTab } from './TransferItems';

export interface SaleLoanItemsTableProps {
  saleLoansWithProjections: {
    sourceLoans: SaleLoanWithProjections[];
    recipientLoans: SaleLoanWithProjections[];
  };
  grats: {
    sourceGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
    recipientGrats: GetHypotheticalTransfers_HypotheticalGratFragment[];
  };
  gratAnnuities: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  gratBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  hypotheticalCrts: {
    sourceCrts: GetHypotheticalTransfers_HypotheticalCrtFragment[];
    recipientCrts: GetHypotheticalTransfers_HypotheticalCrtFragment[];
  };
  hypotheticalCrtIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  hypotheticalCrtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  hypotheticalClts: {
    sourceClts: GetHypotheticalTransfers_HypotheticalCltFragment[];
    recipientClts: GetHypotheticalTransfers_HypotheticalCltFragment[];
  };
  hypotheticalCltIncomePayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  hypotheticalCltBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  hypotheticalInsurance: {
    sourceInsurance: GetHypotheticalTransfers_HypotheticalInsuranceFragment[];
    recipientInsurance: GetHypotheticalTransfers_HypotheticalInsuranceFragment[];
  };
  qprtBeneficiaryPayments: GetHypotheticalTransfers_EstateWaterfallTransferFragment[];
  direction: TransferTab;
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[];
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void;
}

export function StrategyItemsTable(props: SaleLoanItemsTableProps) {
  const { direction } = props;
  const allowEditingHypotheticalTransfers = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MODIFY_HYPOTHETICAL_TRANSFERS
  );

  const strategyItems = useStrategyItems(props);

  const netAmount = useMemo(() => {
    return strategyItems.reduce((acc, { value }) => {
      return acc.plus(value);
    }, new Decimal(0));
  }, [strategyItems]);

  const isOutbound = direction === TransferTab.Outbound;
  const items = strategyItems.map(({ item }) => item);
  return (
    <Stack>
      <Stack p={1.5} direction="row" bgcolor={COLORS.GRAY[100]}>
        <Box>
          <Typography
            variant="title-xs"
            component="p"
            color={COLORS.TEXT.SECONDARY}
          >
            Strategies
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="column"
        sx={{
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        {strategyItems.length === 0 && <EmptyListItem>No items</EmptyListItem>}
        <GroupedHeaderList
          rowGroups={[{ items }]}
          withDividers
          ItemComponent={({ item }) => (
            <TableCell
              RightContent={
                // right now, the only RightContent is the edit action
                // we only want to show the edit action if the user has
                // permission to edit hypothetical transfers
                item.context &&
                allowEditingHypotheticalTransfers && (
                  <TableCellContext {...item.context} />
                )
              }
              {...item}
            />
          )}
        />
        <Stack
          sx={{
            borderTop: `2px solid ${COLORS.NAVY[600]}`,
            px: 1.5,
            py: 2.5,
          }}
          direction="row"
          justifyContent="space-between"
        >
          <Typography variant="title-s" component="span">
            Transferred {isOutbound ? 'out' : 'in'}
          </Typography>
          <Typography
            variant="title-s"
            component="span"
            sx={{
              color: isOutbound ? COLORS.MATH.NEGATIVE : undefined,
            }}
          >
            {formatCurrencyNoDecimalsAccounting(
              netAmount.times(isOutbound ? -1 : 1)
            )}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
